import {
  Control,
  FieldErrors,
  UseFormClearErrors,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form';

import { DecomPolicyFormValues } from '../decom-policy-form-layout.tsx';
import { VaultMatterModel } from '../../../../../api/vault-api.ts';
import VaultMatterHeader from './shared/vault-matter-header.tsx';
import DecomNotifyUsersInput from './shared/decom-notify-users-input.tsx';
import DecomReportRecipientInput from './shared/decom-report-recipient-input.tsx';
import VaultExportConfigSkeleton from './shared/vault-export-config-skeleton.tsx';
import VaultTimezoneSelect from './shared/vault-timezone-select.tsx';
import VaultDateRangePicker from './shared/vault-date-range-picker.tsx';
import { DestUser } from '../../../../../../types.ts';

import { ActionErrorEntry } from '../decom-policy-utils.tsx';

import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import { useTheme } from '@mui/material/styles';

export interface VaultGeminiExportConfigProps {
  matter?: VaultMatterModel;
  isLoading?: boolean;
  day: number;
  idx: number;
  watch: UseFormWatch<DecomPolicyFormValues>;
  setValue: UseFormSetValue<DecomPolicyFormValues>;
  control: Control<DecomPolicyFormValues>;
  errors: FieldErrors<DecomPolicyFormValues>;
  clearErrors: UseFormClearErrors<DecomPolicyFormValues>;
  setActionTypesWithErrors: React.Dispatch<
    React.SetStateAction<ActionErrorEntry[]>
  >;
  destUserFields: (DestUser & { id: string })[];
  destUserAppend: (value: DestUser) => void;
  destUserRemove: (index?: number) => void;
  destUserUpdate: (index: number, value: DestUser) => void;
}

export default function VaultGeminiExportConfig({
  matter,
  isLoading,
  day,
  idx,
  watch,
  setValue,
  control,
  errors,
  clearErrors,
  setActionTypesWithErrors,
  destUserFields,
  destUserAppend,
  destUserRemove,
  destUserUpdate,
}: VaultGeminiExportConfigProps) {
  const theme = useTheme();
  const handleBack = () => {
    setValue(`actions.${day}.actions.${idx}.matterId`, '', {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  if (isLoading) return <VaultExportConfigSkeleton onBack={handleBack} />;

  if (!matter) return null;

  return (
    <Stack gap={3} sx={{ mt: 1 }}>
      <VaultMatterHeader matterName={matter.name} onBack={handleBack} />

      <Stack direction="column" sx={{ px: 2, pb: 3 }}>
        <Stack direction="column" gap={3}>
          <DecomNotifyUsersInput
            day={day}
            idx={idx}
            control={control}
            watch={watch}
            setValue={setValue}
            clearErrors={clearErrors}
          />

          <DecomReportRecipientInput
            day={day}
            idx={idx}
            control={control}
            watch={watch}
            setValue={setValue}
            clearErrors={clearErrors}
            errors={errors}
            actionType="vaultGeminiExport"
            setActionTypesWithErrors={setActionTypesWithErrors}
            destUserFields={destUserFields}
            destUserAppend={destUserAppend}
            destUserRemove={destUserRemove}
            destUserUpdate={destUserUpdate}
          />
        </Stack>
        <Divider
          sx={{
            mb: 2,
            mt: 1.5,
            borderColor: theme.palette.semantic.outline.light,
          }}
        />

        <Stack direction="column" gap={2}>
          <Stack direction="row" gap={4}>
            <VaultTimezoneSelect day={day} idx={idx} control={control} />
          </Stack>
          <Stack direction="row" gap={4}>
            <VaultDateRangePicker
              day={day}
              idx={idx}
              control={control}
              errors={errors}
              label="Date Sent (optional)"
              tooltipText="Search for conversations within the date range"
            />
          </Stack>
        </Stack>
      </Stack>
    </Stack>
  );
}
